import type { BindGeneration } from "./scroll-engine";

/**
 * BindBatcher — coalesces per-index bind requests between animation frames.
 *
 * ScrollEngine enqueues a bind request per index as reconciles happen, then
 * drains the batch once per `$$.measure` frame and dispatches `onBind`. Two
 * rules carry the engine's correctness here and are easy to get subtly wrong,
 * so they live in this DOM-less unit where they can be exercised directly:
 *
 *  1. STICKY ENQUEUE. A queued full rebind (`offsetChangedOnly === false`) is
 *     not downgraded by a later partial (offset-only) request for the same
 *     index — full subsumes partial. A queued partial, conversely, may be
 *     upgraded to full.
 *
 *  2. DRAIN-TIME RESOLUTION. The element bound for an index is whatever
 *     occupies that index *at drain time*, not the element captured at enqueue
 *     time. A pooled element can be swapped between two reconciles that both
 *     land in one batch (recycle E1 at index i, then add a different E2 at i).
 *     The sticky rule keeps E1's record, so binding the captured element would
 *     skip — and strand — the swapped-in E2 (it renders but never binds). On
 *     such a swap the bind is forced full: an offset-only bind would leave E2
 *     showing E1's stale content.
 *
 * The batcher is generic over the element type `T` and holds no DOM or fastdom
 * references; the caller supplies the current occupant of an index via the
 * `resolveCurrent` callback passed to {@link drain}.
 */
export class BindBatcher<T> {
  private readonly pending = new Map<
    number,
    { t: T; offsetChangedOnly: boolean; generation: BindGeneration }
  >();

  get size(): number {
    return this.pending.size;
  }

  get isEmpty(): boolean {
    return this.pending.size === 0;
  }

  /** The epoch of the bind queued for `index`; `null` when none is queued. */
  generationAt(index: number): Option<BindGeneration> {
    return this.pending.get(index)?.generation ?? null;
  }

  /** Queued work for this index; unrelated work cannot fulfill its bind. */
  has(index: number): boolean {
    return this.pending.has(index);
  }

  /**
   * Queue (or coalesce) a bind request for `index`. Replaces an existing record
   * only when there is none yet, or the existing one is a partial that this
   * request can upgrade — a queued full always wins (rule 1).
   *
   * `generation` is the render epoch this request belongs to (see
   * interruptible-rendering). It rides the instruction through {@link drain} and
   * the re-enqueued tail so a superseded render's deferred binds can be dropped
   * in the drain loop with no cross-frame ambient. Generations are monotonic, so
   * on a sticky-keep (existing full subsumes a new partial) the generation is
   * still refreshed upward — a later render that still wants this index must not
   * see it dropped as stale.
   *
   * `generation` is required. It carried a `= 0` default that no production
   * caller used — both engine call sites pass the live epoch — and a default
   * there means an omitted argument silently becomes the INERT epoch, which is
   * the one value that can never be dropped. A bind that quietly opts out of
   * interruptibility is the failure this batcher exists to prevent.
   */
  enqueue(
    index: number,
    t: T,
    offsetChangedOnly: boolean,
    generation: BindGeneration,
  ): void {
    const existing = this.pending.get(index);
    if (!existing || existing.offsetChangedOnly) {
      this.pending.set(index, { t, offsetChangedOnly, generation });
    } else if (generation > existing.generation) {
      existing.generation = generation;
    }
  }

  /**
   * Snapshot and clear the queue, resolving each index to its current occupant
   * (rule 2). `resolveCurrent` returns the element now at an index, or null if
   * the index is no longer rendered (those are dropped). On a swap since
   * enqueue the bind is forced full.
   */
  drain(
    resolveCurrent: (index: number) => Option<T>,
  ): BindBatcher.Instruction<T>[] {
    const snapshot = [...this.pending.entries()];
    this.pending.clear();
    const instructions: BindBatcher.Instruction<T>[] = [];
    for (const [index, e] of snapshot) {
      const current = resolveCurrent(index);
      if (current == null) continue;
      const offsetChangedOnly = current === e.t && e.offsetChangedOnly;
      instructions.push({
        index,
        t: current,
        offsetChangedOnly,
        generation: e.generation,
      });
    }
    return instructions;
  }
}

export namespace BindBatcher {
  /** A resolved bind request produced by {@link BindBatcher.drain}. */
  export type Instruction<T> = {
    index: number;
    t: T;
    offsetChangedOnly: boolean;
    /** Render epoch this bind belongs to; {@link BindGeneration.ZERO} when
     *  interruptible render is off. */
    generation: BindGeneration;
  };
}
